import { submitForm } from '../utils/form-submit';
import { withProductPicker } from '../utils/product-picker';
import { showToast } from '../utils/toast';

const MAX_SAMPLES = 2;

/**
 * Sample service form: pick a product, then up to two of the samples that
 * product is available in.
 *
 * `productSamples` maps each product id to the sample ids it offers, so the
 * sample list narrows from state rather than from data attributes in the DOM.
 */
export function sampleServiceForm({ productSamples = {}, ...pickerConfig } = {}) {
  return withProductPicker(pickerConfig, {
    productSamples,
    selectedSampleIds: [],

    get availableSampleIds() {
      return this.productSamples[this.selectedProductId] ?? [];
    },

    get canChooseSamples() {
      return this.availableSampleIds.length > 0;
    },

    get isComplete() {
      return this.selectedSampleIds.length > 0;
    },

    init() {
      this.applyDeepLink();
    },

    /** A `?product_id=` link from a product page preselects that product. */
    applyDeepLink() {
      const productId = new URLSearchParams(window.location.search).get('product_id');

      if (!productId || !this.applyDeepLinkedProduct(Number(productId))) {
        return;
      }

      this.$nextTick(() => {
        this.$refs.picker
          ?.querySelector(`.product-choice[data-product-id="${productId}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },

    /** Samples belong to a product, so a different product clears them. */
    onProductChange() {
      this.selectedSampleIds = [];
    },

    /** With no product chosen the full range stays on show. */
    isSampleVisible(id) {
      return !this.canChooseSamples || this.availableSampleIds.includes(id);
    },

    isSampleSelected(id) {
      return this.selectedSampleIds.includes(id);
    },

    toggleSample(id) {
      if (this.isSampleSelected(id)) {
        this.selectedSampleIds = this.selectedSampleIds.filter((sampleId) => sampleId !== id);

        return;
      }

      if (this.selectedSampleIds.length >= MAX_SAMPLES) {
        showToast(window.js_localizations.max_samples_message);

        return;
      }

      this.selectedSampleIds = [...this.selectedSampleIds, id];
    },

    submit() {
      submitForm({
        form: this.$refs.form,
        url: window.js_localizations.sample_submission_url,
        extraFields: {
          product_id: this.selectedProductId ?? '',
          sample_ids: this.selectedSampleIds.join(','),
        },
        event: 'generate_lead',
        eventData: { form_name: 'sample_service_form', lead_type: 'sample_request' },
        onSuccess: () => {
          this.$refs.form.reset();
          this.selectProduct(null);
          this.selectedSampleIds = [];
          window.scrollTo({ top: 0, behavior: 'smooth' });
        },
      });
    },
  });
}
